import React, { useEffect, useState } from "react";
import ReactModal from "react-modal";
import { Button, Icon, IconButton } from "../../components/ui";

/**
 * Confirmation dialog for "switch Xero organisation".
 *
 * Replaces the browser's `window.confirm`, which rendered as a system pop-up at
 * the top of the screen with no product styling — and, worse, could say nothing
 * about WHICH document was about to be voided or WHERE it would land. This
 * dialog names both organisations and the document, so the rep can see what the
 * switch will actually do before it happens.
 *
 * It also owns the OUTCOME, not just the decision: the request runs while the
 * dialog stays open, and a failure is shown inline here instead of a detached
 * toast. That matters because the backend's reasons are actionable ("Xero will
 * not void an invoice that still has payments", "un-reconcile it in Xero
 * first") and were previously easy to miss.
 *
 * THE `invoice_missing` ESCALATION (order/invoice flow only): when Xero has
 * never heard of the invoice in the org we resolved to, the backend returns 409
 * with `invoice_missing` rather than voiding blind — "not found here" is not
 * "does not exist anywhere", and dropping the linkage could orphan a live
 * invoice in another org. The UI previously had no way to answer that, so such
 * an order could never be moved by any sequence of clicks (this is what stranded
 * order 1327 on production). The second confirmation below is that answer: tick
 * it and the retry sends `clear_missing_invoice`.
 *
 * Styling is INLINE for the same reason as RemovePaymentModal — a .scss file is
 * not reliably applied to a portal-rendered modal, which left earlier dialogs
 * unstyled and full-width. Tokens mirror RC OS v3 dark.
 */

const C = {
  bg: "#141414",
  panel: "#1a1a1a",
  headline: "#ffffff",
  text: "#E4E4E4",
  para: "#B7B7B7",
  muted: "#797979",
  outline: "#303030",
  red: "#ED6A5F",
  amber: "#E0A458",
  purple: "#7145D0",
  font: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
};

const S = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0, 0, 0, 0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px",
    zIndex: 1000,
  },
  content: {
    position: "relative",
    inset: "auto",
    width: "100%",
    maxWidth: "460px",
    background: C.bg,
    border: "none",
    borderRadius: "16px",
    padding: "22px",
    color: C.text,
    fontFamily: C.font,
    maxHeight: "90vh",
    overflow: "auto",
    boxSizing: "border-box",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "12px",
  },
  h2: {
    fontFamily: C.font,
    fontSize: "18px",
    fontWeight: 600,
    color: C.headline,
    margin: 0,
  },
  body: {
    color: C.para,
    fontSize: "14px",
    lineHeight: 1.5,
    margin: "0 0 16px",
  },
  // from → to, so the switch is legible at a glance rather than described.
  route: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    background: C.panel,
    border: `1px solid ${C.outline}`,
    borderRadius: "8px",
    padding: "12px 14px",
    margin: "0 0 16px",
    fontSize: "14px",
    flexWrap: "wrap",
  },
  org: { color: C.headline, fontWeight: 500 },
  arrow: { color: C.muted },
  docChip: {
    display: "inline-block",
    background: "rgba(113, 69, 208, 0.15)",
    border: `1px solid ${C.purple}`,
    borderRadius: "6px",
    padding: "2px 8px",
    color: C.text,
    fontSize: "13px",
  },
  warn: {
    background: "rgba(224, 164, 88, 0.1)",
    border: `1px solid ${C.amber}`,
    color: C.amber,
    borderRadius: "8px",
    padding: "10px 12px",
    fontSize: "13px",
    lineHeight: 1.5,
    marginBottom: "16px",
  },
  error: {
    background: "rgba(237, 106, 95, 0.1)",
    border: `1px solid ${C.red}`,
    color: C.red,
    borderRadius: "8px",
    padding: "10px 12px",
    fontSize: "13px",
    lineHeight: 1.5,
    marginBottom: "16px",
  },
  confirm: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    background: C.panel,
    border: `1px solid ${C.outline}`,
    borderRadius: "8px",
    padding: "12px 14px",
    margin: "0 0 16px",
    cursor: "pointer",
    color: C.text,
    fontSize: "14px",
  },
  checkbox: {
    marginTop: "2px",
    width: "16px",
    height: "16px",
    accentColor: C.purple,
    cursor: "pointer",
    flexShrink: 0,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "8px",
    marginTop: "8px",
  },
};

/**
 * The admin-facing name of a connected org, for a connection id. Falls back to a
 * readable placeholder rather than printing a bare id at the rep — an unnamed
 * org usually means the list hasn't loaded, not that it has no name.
 */
export function orgLabel(organisations, id) {
  if (!id) return null;
  const match = (organisations || []).find((o) => Number(o.id) === Number(id));
  return match?.org_label || match?.tenant_name || null;
}

export function SwitchXeroOrgModal({
  open,
  // "invoice" (order flow) or "purchase order" (sell/request flow).
  documentLabel = "invoice",
  documentNumber = null,
  fromLabel = null,
  toLabel = null,
  submitting = false,
  error = null,
  // { invoice_number, organisation } when the backend returned 409 invoice_missing.
  missingInvoice = null,
  onCancel,
  onConfirm,
}) {
  const [clearMissing, setClearMissing] = useState(false);

  // A fresh escalation must be ticked again — never carry a previous consent
  // into a new switch.
  useEffect(() => {
    if (open) setClearMissing(false);
  }, [open, missingInvoice?.invoice_number]);

  const Doc = documentLabel.charAt(0).toUpperCase() + documentLabel.slice(1);
  const blocked = submitting || (!!missingInvoice && !clearMissing);

  return (
    <ReactModal
      isOpen={!!open}
      // No backdrop / ESC dismissal mid-request: the switch is a multi-step Xero
      // operation and closing here would hide its outcome.
      onRequestClose={submitting ? undefined : onCancel}
      ariaHideApp={false}
      style={{ overlay: S.overlay, content: S.content }}
    >
      <div style={S.header}>
        <h2 style={S.h2}>Switch Xero organisation</h2>
        {!submitting && (
          <IconButton label="Close" variant="ghost" size="sm" onClick={onCancel}>
            <Icon name="X" size="sm" />
          </IconButton>
        )}
      </div>

      <div style={S.route}>
        <span style={S.org}>{fromLabel || "Current organisation"}</span>
        <span style={S.arrow}>→</span>
        <span style={S.org}>{toLabel || "Selected organisation"}</span>
        {documentNumber && <span style={S.docChip}>{documentNumber}</span>}
      </div>

      <p style={S.body}>
        The {documentLabel}
        {documentNumber ? ` (${documentNumber})` : ""} and any payments recorded
        against it will be{" "}
        <strong style={{ color: C.headline }}>VOIDED</strong> in{" "}
        {fromLabel || "the current organisation"}, then re-created in{" "}
        {toLabel || "the selected organisation"} under a new number.
      </p>

      <div style={S.warn}>
        {Doc} numbers are not reusable — the voided one stays in{" "}
        {fromLabel || "the old organisation"} as a void record, and the client
        sees a different number on the re-issued document.
      </div>

      {missingInvoice && (
        <>
          <div style={S.error}>
            Xero has no {documentLabel}{" "}
            <strong>{missingInvoice.invoice_number || documentNumber}</strong> in{" "}
            {missingInvoice.organisation || fromLabel || "that organisation"}, so
            there is nothing to void there. It may still be live in a{" "}
            <strong>different</strong> connected organisation — check before
            continuing, because dropping the reference would leave that one
            orphaned.
          </div>
          <label style={S.confirm}>
            <input
              type="checkbox"
              style={S.checkbox}
              checked={clearMissing}
              onChange={(e) => setClearMissing(e.target.checked)}
              disabled={submitting}
            />
            <span>
              I have confirmed this {documentLabel} exists in no connected
              organisation — drop the stale reference and switch.
            </span>
          </label>
        </>
      )}

      {error && !missingInvoice && <div style={S.error}>{error}</div>}

      <div style={S.actions}>
        <Button
          variant="secondary"
          data-btn="xero-cancel-switch"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </Button>
        {/* Label swap kept (not `loading`): the in-flight state must read
            "Switching…" and stay a disabled control — see the modal's tests. */}
        <Button
          variant="danger"
          data-btn="xero-confirm-switch"
          onClick={() => onConfirm?.({ clearMissingInvoice: clearMissing })}
          disabled={blocked}
        >
          {submitting
            ? "Switching…"
            : missingInvoice
              ? "Drop reference & switch"
              : `Void & re-create ${documentLabel}`}
        </Button>
      </div>
    </ReactModal>
  );
}

export default SwitchXeroOrgModal;
